import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useItemsStore, useSessionStore, useSitesStore } from '@/stores';
import type IDelivery from "@/models/responses/IDelivery";
import { DELIVERY_HEAD_STATUS } from "@/models/Enums";
import { IDeliveryAttachmentReduced } from "@/models/responses/IDelivery";
import Delivery, { DeliveryLine } from "@/models/frontend/Delivery";

export const useDeliveriesStore = defineStore("deliveries", {
  state: () => ({
    error: "" as any,
    data: [] as Delivery[],
    delivery: null as Delivery | null,
    attachments: [] as IDeliveryAttachmentReduced[],
  }),
  actions: {
    async fetchAll(includeHistory: boolean = false) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      this.error = "";
      await api
        .get<IDelivery[]>(`v1.0/deliveries?siteId=${selectedSiteId.value}&status=${DELIVERY_HEAD_STATUS.inProgress}${includeHistory ? "&forcedIncludeTimeSpan=30.00:00:00" : ""}`)
        .then((response) => (this.data = response.data.map((delivery) => this.mapToFrontend(delivery))))
        .catch((error) => (this.error = error));
    },
    async fetch(id: number) {
      this.error = "";
      this.delivery = null;
      await api
        .get<IDelivery>(`v1.0/deliveries/${id}`)
        .then((response) => (this.delivery = this.mapToFrontend(response.data)))
        .catch((error) => (this.error = error));
    },
    async fetchAttachments(id: number) {
      this.error = "";
      this.attachments = [];
      await api
        .get<IDeliveryAttachmentReduced[]>(`v1.0/deliveries/${id}/attachments`)
        .then((response) => (this.attachments = response.data))
        .catch((error) => (this.error = error));
    },
    async fetchByCode(code: string) {
      this.error = "";
      this.delivery = null;
      await api
        .get<IDelivery>(`v1.0/deliveries/_${code}`)
        .then((response) => (this.delivery = this.mapToFrontend(response.data)))
        .catch((error) => (this.error = error));
    },
    async complete(id: number) {
      this.error = "";
      await api
        .put(`v1.0/deliveries/${id}/complete`)
        .then(() => {
          this.fetchAll();
        })
        .catch((error) => (this.error = error));
    },
    async cancel(id: number) {
      this.error = "";
      await api
        .put(`v1.0/deliveries/${id}/cancel`)
        .then(() => {
          this.fetchAll();
        })
        .catch((error) => (this.error = error));
    },
    async delete(id: number) {
      this.error = "";
      await api
        .delete(`v1.0/deliveries/${id}`)
        .then(() => {
          this.data = this.data.filter((delivery) => delivery.id !== id);
        })
        .catch((error) => (this.error = error));
    },
    mapToFrontend(delivery: IDelivery) {
      const site = useSitesStore().getById(delivery.siteId);
      const itemsStore = useItemsStore();
      const deliveryLines = delivery.lines.map((deliveryLine) => {
        const item = itemsStore.getById(deliveryLine.itemId);
        return new DeliveryLine(item, deliveryLine.lot, deliveryLine.bestBefore, deliveryLine.expiry, deliveryLine.quantity);
      });
      return new Delivery(
        delivery.id,
        site,
        delivery.code,
        delivery.description,
        delivery.status,
        deliveryLines,
        delivery.audit,
      );
    },
  },
  getters: {
    getById:
      (state) =>
        (id: number): Delivery | undefined => {
          if (state.delivery?.id === id) return state.delivery;
          return <Delivery>(
            (state.data as Delivery[]).find((delivery) => delivery.id === id)
          );
        },
    getByCode:
      (state) =>
        (code: string, siteId: number): Delivery | undefined => {
          if (state.delivery?.code === code) return state.delivery;
          return <Delivery>(
            (state.data as Delivery[]).find(
              (delivery) => delivery.code === code && delivery.site.id == siteId
            )
          );
        },
  },
});
